import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { Loader } from "semantic-ui-react";
import { yupResolver } from "@hookform/resolvers/yup";
import ObservationsAction from "@common/redux/actions/ObservationsAction";
import LoginAction from "@common/redux/actions/LoginAction";
import { observationsSchema } from "@common/components/helpers/validation-schemas/observationsSchema";
import ObservationsForm from "@common/components/shared/Observations/ObservationsForm";
import ObservationsClient from "@common/api/ObservationsClient";
import useEventDetailsGrid from "@hooks/useEventDetailsGrid";
import { parseObservationResponse } from "@common/components/helpers/compliance";
import ComplianceHeader from "../ComplianceHeader";
import useComplianceView from "@hooks/useComplianceView";
import { handleError } from "@common/components/helpers/error";
import useLinkSession from "@common/components/hooks/useLinkSession";
import useLinkedAlert from "@common/components/hooks/useLinkedAlert";

function useObservationContainer() {
  const data = useSelector((state) => ObservationsAction.getStatePath(state));
  const { setViewData } = useComplianceView();
  const {
    activeRow: {
      event: { id },
    },
  } = useEventDetailsGrid();
  const { getComplianceSessionsByGuids } = useLinkSession();
  const { setState } = useLinkedAlert();

  const methods = useForm({
    resolver: yupResolver(observationsSchema),
    defaultValues: data,
    mode: "all",
  });

  const { reset } = methods;

  const loadRecord = (isRead = true) => {
    if (!id) {
      return;
    }

    setViewData({ formLoading: true });

    const fnRead = isRead
      ? ObservationsClient.Read
      : ObservationsClient.ReadForUpdate;

    fnRead(id).then(
      ({ record }) => {
        const {
          site: { guid },
          sessionGuids,
          alerts,
        } = record;
        LoginAction.dispatch({ siteGuid: guid, isInReadMode: isRead });
        reset(parseObservationResponse(record));
        getComplianceSessionsByGuids("", sessionGuids);
        setState({ linkedAlertData: alerts });
        setViewData({ formLoading: false });
      },
      (error) => {
        setViewData({ formLoading: false });
        handleError(error);
      },
    );
  };

  useEffect(loadRecord, [id]);

  return {
    data,
    methods,
    loadRecord,
  };
}

const ObservationContainer = () => {
  const { methods, loadRecord } = useObservationContainer();
  const { formLoading } = useComplianceView();

  if (formLoading) {
    return <Loader active />;
  }

  return (
    <FormProvider {...methods}>
      <ComplianceHeader title="Observation" loadRecord={loadRecord} />
      <ObservationsForm />
    </FormProvider>
  );
};

export default ObservationContainer;
